import React, { useState } from "react";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import {
  tokens,
  StatusPill,
  CodeChip,
  PatientStrip,
  SectionPanel,
  ClinicalEmptyState,
} from "../../shared/clinical-design";
import { ADHERENCE_FLAGS, PDC_THRESHOLD, pdcTone } from "../common/constants";

const LABEL_SX = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: tokens.ink3,
};
const VALUE_SX = {
  fontFamily: tokens.fontMono,
  fontVariantNumeric: "tabular-nums",
  fontSize: 14,
  color: tokens.ink,
};

function Field({ label, children }) {
  return (
    <Box>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Typography component="div" sx={VALUE_SX}>{children ?? "—"}</Typography>
    </Box>
  );
}

/** Barra horizontal compacta del PDC contra el umbral 80% (sin gráficos pesados). */
function PdcBar({ pdc }) {
  const tone = pdcTone(pdc);
  const color = tokens[tone] || tokens.ink;
  return (
    <Box sx={{ position: "relative", mt: 0.5 }}>
      <Box
        sx={{
          height: 10,
          borderRadius: "4px",
          bgcolor: tokens.surface2,
          border: `1px solid ${tokens.line}`,
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: `${pdc}%`, height: "100%", bgcolor: color }} />
      </Box>
      {/* Marca del umbral 80% */}
      <Box
        sx={{
          position: "absolute",
          top: -2,
          bottom: -2,
          left: `${PDC_THRESHOLD}%`,
          width: "2px",
          bgcolor: tokens.ink2,
        }}
      />
      <Typography sx={{ fontSize: 11, color: tokens.ink3, mt: 0.5 }}>
        Threshold {PDC_THRESHOLD}% (Star/HEDIS)
      </Typography>
    </Box>
  );
}

/**
 * Detalle de adherencia: PDC vs umbral, flags, barreras y acciones de outreach
 * (estado local hasta que exista el backend pharmacy).
 */
export default function AdherenceDetailPanel({ row, locationLabel }) {
  const [actionsTaken, setActionsTaken] = useState([]);

  if (!row) {
    return (
      <SectionPanel title="Adherence detail">
        <ClinicalEmptyState message="Select a patient from the adherence queue to review the regimen." />
      </SectionPanel>
    );
  }

  const tone = pdcTone(row.pdc);
  const takeAction = (label) =>
    setActionsTaken((prev) => (prev.includes(label) ? prev : [...prev, label]));

  return (
    <Stack spacing={1.5}>
      <PatientStrip
        patient={{
          name: row.name,
          age: row.age,
          gender: row.gender,
          cipId: row.cipId,
          location: locationLabel,
          pcp: row.pcpName,
        }}
        actions={<StatusPill tone={tone} label={`PDC ${row.pdc}%`} />}
      />

      <SectionPanel title={`Adherence — ${row.therapeuticClass}`}>
        <Grid container spacing={1.5} alignItems="flex-end">
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography sx={LABEL_SX}>Proportion of days covered (simulated)</Typography>
            <PdcBar pdc={row.pdc} />
          </Grid>
          <Grid size={{ xs: 4, md: 2 }}>
            <Field label="Medications">{row.medications}</Field>
          </Grid>
          <Grid size={{ xs: 4, md: 2 }}>
            <Field label="Refill gaps">
              <Box component="span" sx={{ color: row.refillGaps > 0 ? tokens.crit : tokens.ok }}>
                {row.refillGaps}
              </Box>
            </Field>
          </Grid>
          <Grid size={{ xs: 4, md: 1 }}>
            <Field label="Labs">{row.recentLabs === "Y" ? "Yes" : "No"}</Field>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
          {row.flags.map((f) => {
            const meta = ADHERENCE_FLAGS[f];
            return meta ? <StatusPill key={f} tone={meta.tone} label={meta.label} dense /> : null;
          })}
          {row.flags.length === 0 && (
            <StatusPill tone="ok" label="No adherence flags" dense />
          )}
        </Stack>
      </SectionPanel>

      <SectionPanel title="Clinical context">
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Field label="Health status">{row.healthStatus}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Field label="Last visit">{row.lastVisit}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Field label="Open care gaps">{row.openCareGaps ?? 0}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Field label="ER events">{row.erEvents ?? 0}</Field>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Field label="Conditions">
              <Box component="span" sx={{ fontFamily: tokens.fontUI, fontSize: 13, color: tokens.ink2 }}>
                {row.conditions || "—"}
              </Box>
            </Field>
          </Grid>
          {row.barrier && (
            <Grid size={{ xs: 12 }}>
              <Field label="Primary barrier (reported)">
                <StatusPill tone="warn" label={row.barrier} dense />
              </Field>
            </Grid>
          )}
        </Grid>
      </SectionPanel>

      <SectionPanel title="Outreach actions">
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          {["Send refill reminder", "Schedule med review", "Request med sync"].map((label) => (
            <Button
              key={label}
              variant={actionsTaken.includes(label) ? "outlined" : "contained"}
              size="small"
              disableElevation
              disabled={actionsTaken.includes(label)}
              onClick={() => takeAction(label)}
              sx={{
                bgcolor: actionsTaken.includes(label) ? "transparent" : tokens.primary,
                "&:hover": { bgcolor: tokens.primary700 },
                textTransform: "none",
                fontSize: 13,
              }}
            >
              {actionsTaken.includes(label) ? `${label} — queued` : label}
            </Button>
          ))}
        </Stack>
        {actionsTaken.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap", rowGap: 1 }}>
            {actionsTaken.map((a) => (
              <CodeChip key={a} code={a} tone="ok" />
            ))}
          </Stack>
        )}
        <Typography sx={{ fontSize: 11.5, color: tokens.ink3, mt: 1 }}>
          Actions are local-only in this preview — they will create outreach tasks
          via the pharmacy backend (Module H engine) once available.
        </Typography>
      </SectionPanel>
    </Stack>
  );
}
